import { useEffect, useState } from 'react'
import {
  EARLIEST_DATA_DAY,
  formatRange,
  matchingPreset,
  type DateRange,
  type RangePreset,
  type RangePresetLabel,
} from '../lib/ranges'

const BASE =
  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors'
const ACTIVE =
  'border-salsa-500 bg-salsa-500/10 text-salsa-700 dark:border-salsa-500/50 dark:text-salsa-300'
const IDLE =
  'border-slate-200 text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

type CustomMode = 'date' | 'range' | null

function todayIso(now: Date): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Shared period controls for the analysis screens.
 *
 * The standard presets remain one-click. Custom date and custom range cover the
 * cases the presets cannot: one-off days and arbitrary inclusive ranges. Both
 * stay clamped to the modelled data floor and today.
 */
export function PeriodControls({
  value,
  onChange,
  presets,
  now,
}: {
  value: DateRange
  onChange: (range: DateRange) => void
  presets: RangePreset[]
  now: Date
}) {
  const activePreset = matchingPreset(value, now)
  const [mode, setMode] = useState<CustomMode>(() =>
    activePreset ? null : value.from === value.to ? 'date' : 'range',
  )
  const today = todayIso(now)

  useEffect(() => {
    if (activePreset) setMode(null)
  }, [activePreset])

  const choosePreset = (preset: RangePreset) => {
    setMode(null)
    onChange({ from: preset.from, to: preset.to, preset: preset.label as RangePresetLabel })
  }

  const chooseCustomDate = () => {
    setMode('date')
    if (value.from !== value.to) onChange({ from: value.to, to: value.to })
  }

  const chooseCustomRange = () => setMode('range')

  const changeDate = (next: string) => {
    if (!next) return
    onChange({ from: next, to: next })
  }

  const changeFrom = (next: string) => {
    if (!next) return
    onChange({ from: next, to: next > value.to ? next : value.to })
  }

  const changeTo = (next: string) => {
    if (!next) return
    onChange({ from: next < value.from ? next : value.from, to: next })
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          Period
        </span>
        {/* Twelve presets stacked into four rows on a phone; one sideways rail keeps
            the numbers in view (D-106). */}
        <div className="rail-x flex flex-nowrap gap-1.5 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
          {presets.map((preset) => {
            const active = !mode && activePreset === preset.label
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => choosePreset(preset)}
                aria-pressed={active}
                className={`${BASE} ${active ? ACTIVE : IDLE}`}
              >
                {preset.label}
              </button>
            )
          })}
          <button
            type="button"
            onClick={chooseCustomDate}
            aria-pressed={mode === 'date'}
            className={`${BASE} ${mode === 'date' ? ACTIVE : IDLE}`}
          >
            Custom date
          </button>
          <button
            type="button"
            onClick={chooseCustomRange}
            aria-pressed={mode === 'range'}
            className={`${BASE} ${mode === 'range' ? ACTIVE : IDLE}`}
          >
            Custom range
          </button>
        </div>
        <span className="w-full text-[11px] text-slate-500 sm:ml-auto sm:w-auto dark:text-slate-400">
          {formatRange(value)}
        </span>
      </div>

      {mode && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {mode === 'date' ? 'Date' : 'From'}
            <input
              type="date"
              value={value.from}
              min={EARLIEST_DATA_DAY}
              max={today}
              onChange={(event) =>
                mode === 'date' ? changeDate(event.target.value) : changeFrom(event.target.value)
              }
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-normal text-slate-700 outline-none focus:border-salsa-400 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            />
          </label>
          {mode === 'range' && (
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              To
              <input
                type="date"
                value={value.to}
                min={value.from}
                max={today}
                onChange={(event) => changeTo(event.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-normal text-slate-700 outline-none focus:border-salsa-400 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
              />
            </label>
          )}
        </div>
      )}
    </>
  )
}
